import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/features/auth/auth-store';
import { authConfig, useAuth } from '@/features/auth/use-auth';
import {
  useBillingPortalLinkQuery,
  useSubscriptionsQuery,
} from '@/features/subscription/subscription.query';
import {
  ChevronDown,
  CreditCardIcon,
  MoonIcon,
  PowerIcon,
  ScaleIcon,
  SunIcon,
  UserPenIcon,
} from 'lucide-react';
import { useUiStorePersistent } from '../store/ui-store';

export function NavDropdown() {
  const { logout } = useAuth();
  const { userEmail } = useAuthStore();
  const { isDarkMode, setIsDarkMode } = useUiStorePersistent();

  const { data: subscriptionsData } = useSubscriptionsQuery({});

  const activeUsageSubscription = subscriptionsData?.content.find(
    (subscription) =>
      subscription.type === 'Usage' &&
      !subscription.trial &&
      subscription.active,
  );

  const { refetch: refetchBillingPortal } = useBillingPortalLinkQuery(
    {
      subscriptionId: activeUsageSubscription?.id || '',
      enabled: false,
    },
  );

  const handleOpenBillingPortal = async () => {
    if (activeUsageSubscription?.id) {
      try {
        const result = await refetchBillingPortal();
        if (result.data?.billingPortalUrl) {
          window.open(result.data.billingPortalUrl, '_blank');
        }
      } catch (error) {
        console.error('Failed to get billing portal link:', error);
      }
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div>
          <Button
            variant="ghost"
            className="flex cursor-pointer items-center gap-2 truncate font-semibold"
          >
            {userEmail}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="bg-card"
      >
        <DropdownMenuItem asChild>
          <a
            href={authConfig.accountSettingsUrl}
            className="w-full cursor-pointer"
            target="_blank"
            rel="noreferrer"
          >
            <UserPenIcon className="mr-1 h-4 w-4" />
            Account Settings
          </a>
        </DropdownMenuItem>

        {activeUsageSubscription && (
          <DropdownMenuItem
            onClick={() => void handleOpenBillingPortal()}
            className="cursor-pointer"
          >
            <CreditCardIcon className="mr-1 h-4 w-4" />
            Billing
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onClick={toggleTheme}
          className="cursor-pointer"
        >
          {isDarkMode ? (
            <>
              <SunIcon className="mr-1 h-4 w-4" />
              Light Mode
            </>
          ) : (
            <>
              <MoonIcon className="mr-1 h-4 w-4" />
              Dark Mode
            </>
          )}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <a
            href={'https://embraceable.ai/agb/'}
            className="w-full cursor-pointer"
            target="_blank"
            rel="noreferrer"
          >
            <ScaleIcon className="mr-1 h-4 w-4" />
            Terms of Service
          </a>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <button
            className="w-full cursor-pointer text-left"
            onClick={() => void logout()}
          >
            <PowerIcon className="mr-1 h-4 w-4" />
            Logout
          </button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
